import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  EyeOff,
  Loader2,
  Plus,
  ShieldCheck,
  Target,
  Trash2,
} from 'lucide-react';
import {
  AuditSummary,
  AuditWindow,
  BlindFinding,
  WindowScore,
  auditSummary,
  drawAuditWindows,
  listAuditWindows,
  submitBlindRead,
} from '../admin';

/**
 * Blinded audit windows (DESIGN.md §11.3(b), §12.7 stage 5).
 *
 * **The only screen in this product that can find a missed finding.** Everywhere else, a reviewer
 * is shown what the engine found and asked to judge it — so a corpus built from that work contains
 * almost no false negatives by construction, and measured sensitivity climbs toward 100% while the
 * true miss rate stays unknown. §11.3 calls this the one mechanism to build if only one gets built.
 *
 * Three things are deliberate and all three are refusals:
 *
 * - **No engine output reaches this screen.** Not the findings, not the counts, not a marker that
 *   something is there. The server sends a span and nothing else, so there is nothing here to
 *   render even by accident. A reader shown "AFib, 0.94" and asked to confirm is not an
 *   independent observer (§11.3(1)).
 * - **"Nothing here" must be pressed.** It is not inferred from an empty list, because a window
 *   nobody read and a window read with nothing in it are different facts (§3.3) — and confusing
 *   them would count real misses as true negatives, inverting the number this produces.
 * - **The windows are drawn once.** An auditor who could re-roll until the windows looked quiet
 *   would defeat the point of a random sample (§11.3(3)).
 *
 * The engine's own reading appears only after the read is submitted, which is the first moment it
 * can no longer influence the judgement.
 */

interface AuditWindowsProps {
  studyId: string;
  onBack: () => void;
}

const CLASSES = [
  'afib',
  'aflutter',
  'vt',
  'svt',
  'pause',
  'bradycardia',
  'tachycardia',
  'artifact',
];

const clock = (value: number) => {
  const total = Math.max(0, Math.round(value));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

interface Draft {
  class: string;
  offsetSeconds: number;
  durationSeconds: number;
}

export const AuditWindows: React.FC<AuditWindowsProps> = ({ studyId, onBack }) => {
  const [windows, setWindows] = useState<AuditWindow[]>([]);
  const [summary, setSummary] = useState<AuditSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [drafts, setDrafts] = useState<Record<string, Draft[]>>({});
  const [scores, setScores] = useState<Record<string, WindowScore>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [listed, scored] = await Promise.all([listAuditWindows(studyId), auditSummary(studyId)]);
      setWindows(listed.windows);
      setSummary(scored);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load audit windows.');
    } finally {
      setLoading(false);
    }
  }, [studyId]);

  useEffect(() => {
    void load();
  }, [load]);

  const draw = async () => {
    setBusyId('draw');
    setError(null);
    try {
      await drawAuditWindows(studyId);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not draw windows.');
    } finally {
      setBusyId(null);
    }
  };

  const addDraft = (window: AuditWindow) =>
    setDrafts((d) => ({
      ...d,
      [window.id]: [...(d[window.id] ?? []), { class: CLASSES[0], offsetSeconds: 0, durationSeconds: 10 }],
    }));

  const updateDraft = (windowId: string, index: number, patch: Partial<Draft>) =>
    setDrafts((d) => ({
      ...d,
      [windowId]: (d[windowId] ?? []).map((f, i) => (i === index ? { ...f, ...patch } : f)),
    }));

  const removeDraft = (windowId: string, index: number) =>
    setDrafts((d) => ({ ...d, [windowId]: (d[windowId] ?? []).filter((_, i) => i !== index) }));

  const submit = async (window: AuditWindow, foundNothing: boolean) => {
    const rows = drafts[window.id] ?? [];
    setBusyId(window.id);
    setError(null);
    try {
      const findings: BlindFinding[] = foundNothing
        ? []
        : rows.map((row) => ({
            class: row.class,
            start_sample: window.start_sample + Math.round(row.offsetSeconds * window.sample_rate_hz),
            end_sample:
              window.start_sample +
              Math.round((row.offsetSeconds + row.durationSeconds) * window.sample_rate_hz),
          }));
      const result = await submitBlindRead(window.id, { findings, found_nothing: foundNothing });
      setScores((s) => ({ ...s, [window.id]: result.score }));
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not record the read.');
    } finally {
      setBusyId(null);
    }
  };

  const pending = windows.filter((w) => !w.already_read);

  return (
    <div className="mx-auto max-w-4xl space-y-4 px-4 py-6">
      <div className="glass-panel flex items-center justify-between rounded-2xl p-4">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="rounded-xl border border-slate-800 bg-slate-900 p-2 text-slate-300 hover:text-white"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h2 className="text-lg font-bold text-white">Blinded audit</h2>
            <p className="text-xs text-slate-400">
              Random stretches, read with the analysis hidden. The only way to find what the engine
              missed.
            </p>
          </div>
        </div>
        {windows.length === 0 && (
          <button
            onClick={() => void draw()}
            disabled={busyId === 'draw'}
            className="flex items-center gap-1.5 rounded-lg border border-cyan-700 bg-cyan-800 px-3 py-2 text-xs font-semibold text-white disabled:opacity-40"
          >
            {busyId === 'draw' ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Target className="h-3.5 w-3.5" />
            )}
            Draw windows
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-xl border border-rose-800 bg-rose-950 px-4 py-2 text-xs text-rose-200">
          {error}
        </div>
      )}

      <div className="rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-2 text-[11px] text-slate-400">
        Windows are drawn at random and only once. Nothing the analysis produced is shown here
        until your reading is recorded.
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          {windows.length === 0 && (
            <p className="rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-3 text-xs text-slate-400">
              No windows drawn for this study yet.
            </p>
          )}

          {windows.map((window) => {
            const rows = drafts[window.id] ?? [];
            const score = scores[window.id];
            return (
              <div key={window.id} className="glass-panel space-y-3 rounded-2xl p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-mono text-sm text-white">
                      {clock(window.start_seconds)} – {clock(window.end_seconds)}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      {Math.round((window.end_seconds - window.start_seconds) / 60)} minutes ·{' '}
                      {window.sample_rate_hz} Hz
                    </div>
                  </div>
                  {window.already_read ? (
                    <span className="flex items-center gap-1 rounded-lg border border-emerald-800 bg-emerald-950 px-2 py-1 text-[11px] text-emerald-200">
                      <CheckCircle2 className="h-3 w-3" /> read
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 rounded-lg border border-slate-700 px-2 py-1 text-[11px] text-slate-400">
                      <EyeOff className="h-3 w-3" /> analysis hidden
                    </span>
                  )}
                </div>

                {score && (
                  <div className="space-y-1 rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-xs">
                    <div className="font-semibold text-slate-200">What the engine had here</div>
                    {Object.entries(score.per_class).length === 0 ? (
                      <div className="text-slate-400">Nothing, and you agreed.</div>
                    ) : (
                      Object.entries(score.per_class).map(([cls, counts]) => (
                        <div key={cls} className="flex justify-between text-slate-300">
                          <span className="font-mono">{cls}</span>
                          <span className="space-x-2">
                            {counts.true_positive > 0 && (
                              <span className="text-emerald-300">
                                {counts.true_positive} agreed
                              </span>
                            )}
                            {counts.false_negative > 0 && (
                              <span className="font-semibold text-amber-300">
                                {counts.false_negative} it missed
                              </span>
                            )}
                            {counts.false_positive > 0 && (
                              <span className="text-slate-400">
                                {counts.false_positive} you did not mark
                              </span>
                            )}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {!window.already_read && (
                  <>
                    {rows.map((row, index) => (
                      <div key={index} className="flex flex-wrap items-center gap-2 text-xs">
                        <select
                          value={row.class}
                          onChange={(e) => updateDraft(window.id, index, { class: e.target.value })}
                          className="rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-slate-200"
                        >
                          {CLASSES.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                        <label className="text-slate-500">at +</label>
                        <input
                          type="number"
                          min={0}
                          value={row.offsetSeconds}
                          onChange={(e) =>
                            updateDraft(window.id, index, { offsetSeconds: Number(e.target.value) })
                          }
                          className="w-20 rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-slate-200"
                        />
                        <label className="text-slate-500">s, lasting</label>
                        <input
                          type="number"
                          min={1}
                          value={row.durationSeconds}
                          onChange={(e) =>
                            updateDraft(window.id, index, {
                              durationSeconds: Number(e.target.value),
                            })
                          }
                          className="w-20 rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-slate-200"
                        />
                        <label className="text-slate-500">s</label>
                        <button
                          onClick={() => removeDraft(window.id, index)}
                          className="rounded-lg border border-slate-700 p-1.5 text-slate-400 hover:text-rose-300"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}

                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <button
                        onClick={() => addDraft(window)}
                        className="flex items-center gap-1.5 rounded-lg border border-slate-700 px-2.5 py-1.5 text-xs text-slate-300 hover:border-slate-500"
                      >
                        <Plus className="h-3.5 w-3.5" /> Add a finding
                      </button>
                      <div className="flex gap-2">
                        {/* Pressed, never inferred. An unread window and an empty one are
                            different facts (§3.3). */}
                        <button
                          onClick={() => void submit(window, true)}
                          disabled={busyId === window.id || rows.length > 0}
                          title={
                            rows.length > 0
                              ? 'Remove the findings above first — a window cannot both have findings and have none.'
                              : undefined
                          }
                          className="flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-medium text-slate-300 hover:border-slate-500 disabled:opacity-40"
                        >
                          <ShieldCheck className="h-3.5 w-3.5" /> Nothing here
                        </button>
                        <button
                          onClick={() => void submit(window, false)}
                          disabled={busyId === window.id || rows.length === 0}
                          className="flex items-center gap-1.5 rounded-lg border border-cyan-700 bg-cyan-800 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
                        >
                          {busyId === window.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <CheckCircle2 className="h-3.5 w-3.5" />
                          )}
                          Record {rows.length} finding{rows.length === 1 ? '' : 's'}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })}

          {summary && summary.windows_scored > 0 && (
            <section className="glass-panel space-y-2 rounded-2xl p-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-400" />
                <h3 className="text-sm font-semibold text-white">
                  Measured sensitivity, including misses
                </h3>
              </div>
              <p className="text-[11px] text-slate-500">
                Every other accuracy figure this platform produces is computed from findings a
                reviewer was shown, so it cannot contain a missed finding and drifts toward 100%
                however many were missed. These numbers can, which is what makes them worth having.
              </p>
              <ul className="space-y-1 text-xs text-slate-300">
                {Object.entries(summary.per_class).map(([cls, counts]) => (
                  <li key={cls} className="flex justify-between border-b border-slate-800 py-1">
                    <span className="font-mono">{cls}</span>
                    <span className="text-slate-400">
                      {counts.sensitivity == null
                        ? 'no cases yet'
                        : `sensitivity ${(counts.sensitivity * 100).toFixed(0)}% · ${counts.false_negative} missed`}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="text-[11px] text-slate-500">
                {summary.windows_scored} window{summary.windows_scored === 1 ? '' : 's'} read ·{' '}
                {summary.windows_agreed_empty} agreed empty
              </div>
            </section>
          )}

          {pending.length === 0 && windows.length > 0 && (
            <p className="rounded-xl border border-emerald-900 bg-emerald-950/40 px-4 py-3 text-xs text-emerald-200">
              Every window for this study has been read.
            </p>
          )}
        </>
      )}
    </div>
  );
};
